import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = ["id", "data-show"]

import { onMounted, ref, toRefs, watch, watchEffect } from 'vue';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import type { Instance } from '@popperjs/core';
import { createPopper } from '@popperjs/core';
import { vTouchTarget } from 'src/directives/TouchTarget/touchTarget';
import { isDevelopmentEnvironment } from '@utils/environmentUtils';
import { useTooltipBehavior } from '@lmt-rpb/InfoTooltip/tooltip-behavior';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { TooltipConfig, createTooltip } from '@lmt-rpb/InfoTooltip/tooltip-config';
import { useMediaQuery } from '@vueuse/core';

export interface Props {
	/** Generate using {@link createTooltip()}. */
	config: TooltipConfig;
	disabled?: boolean;
	color?: 'default' | 'primary';
	/* tooltip will switch between top and bottom if there is not enough space */
	placement?: 'top' | 'bottom';
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InfoTooltip',
  props: {
    config: {},
    disabled: { type: Boolean, default: false },
    color: { default: 'default' },
    placement: { default: 'top' }
  },
  setup(__props: any) {

const props = __props;

const container = ref<HTMLElement>();
const icon = ref<HTMLElement>();
const tooltip = ref<HTMLElement>();
const showTooltip = ref(false);
const tooltipReady = ref(false);
let popper: Instance;

const behavior = useTooltipBehavior(showTooltip, container);
const hasCoarsePointer = useMediaQuery('(pointer: coarse)');
const propsRef = toRefs(props);

watchEffect(() => showTooltip.value && popper?.update());
watch([hasCoarsePointer, propsRef.placement], updatePlacement);
watch(() => props.config.elementId, checkAriaDescribedByExists, { flush: 'post' });

onMounted(async () => {
	setTimeout(() => {
		setupTooltip();
	}, 30);
});

function getCurrentTargetPlacement() {
	if (hasCoarsePointer.value) {
		return 'top';
	}
	return props.placement;
}

function updatePlacement() {
	popper?.setOptions({ placement: getCurrentTargetPlacement() });
}

function checkAriaDescribedByExists() {
	if (!isDevelopmentEnvironment()) {
		return;
	}

	const parentDescriber = icon.value?.closest(`[aria-describedby="${props.config.elementId}"]`);
	if (!parentDescriber) {
		console.error(
			`[InfoTooltip] No parent element has 'aria-describedby'-attribute set for this tooltip.
To fix this: use the exposed variable 'tooltipElementId' and set it on the element the tooltip belongs to logically.

Tooltip location:`,
			icon.value
		);
	}
}

function setupTooltip() {
	if (!icon.value || !tooltip.value) {
		return;
	}

	popper = createPopper(icon.value, tooltip.value, {
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 23],
				},
			},
			{
				name: 'preventOverflow',
				options: {
					boundary: document.querySelector('.list-item.is-verified'),
				},
			},
			{
				name: 'arrow',
				options: {
					padding: 10,
				},
			},
		],
		placement: getCurrentTargetPlacement(),
	});

	tooltipReady.value = true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    ref_key: "container",
    ref: container,
    class: _normalizeClass(["info-tooltip", { 'info-tooltip__primary': _ctx.color === 'primary', 'info-tooltip__open': showTooltip.value }]),
    onMouseleave: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(behavior).onContainerMouseLeave && _unref(behavior).onContainerMouseLeave(...args))),
    onFocusout: _cache[5] || (_cache[5] = 
//@ts-ignore
(...args) => (_unref(behavior).onContainerFocusOut && _unref(behavior).onContainerFocusOut(...args))),
    onMousedown: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(behavior).onContainerMouseDown && _unref(behavior).onContainerMouseDown(...args))),
    onMouseup: _cache[7] || (_cache[7] = 
//@ts-ignore
(...args) => (_unref(behavior).onContainerMouseUp && _unref(behavior).onContainerMouseUp(...args))),
    onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(behavior).onContainerClick($event)))
  }, [
    _withDirectives((_openBlock(), _createElementBlock("button", {
      ref_key: "icon",
      ref: icon,
      type: "button",
      class: "info-tooltip__button",
      "aria-label": "Mehr Informationen",
      disabled: _ctx.disabled,
      onMousedown: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(behavior).onButtonMouseDown && _unref(behavior).onButtonMouseDown(...args))),
      onMouseenter: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(behavior).onButtonMouseEnter && _unref(behavior).onButtonMouseEnter(...args))),
      onFocus: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(behavior).onButtonFocus && _unref(behavior).onButtonFocus(...args))),
      onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(behavior).onButtonClick && _unref(behavior).onButtonClick(...args)))
    }, [
      _createVNode(FaIcon, {
        icon: ['fa-regular', 'fa-circle-info'],
        class: "info-tooltip__icon"
      })
    ], 40 /* PROPS, NEED_HYDRATION */, _hoisted_1)), [
      [_unref(vTouchTarget)]
    ]),
    _createElementVNode("div", {
      id: props.config.elementId,
      ref_key: "tooltip",
      ref: tooltip,
      role: "tooltip",
      class: "info-tooltip__tooltip",
      "data-show": showTooltip.value && tooltipReady.value && !_ctx.disabled
    }, [
      _renderSlot(_ctx.$slots, "default"),
      _cache[9] || (_cache[9] = _createElementVNode("div", {
        class: "info-tooltip__arrow-wrapper",
        "data-popper-arrow": ""
      }, [
        _createElementVNode("div", { class: "info-tooltip__arrow" }),
        _createElementVNode("div", { class: "info-tooltip__mouse-guide" })
      ], -1 /* HOISTED */))
    ], 8 /* PROPS */, _hoisted_2)
  ], 34 /* CLASS, NEED_HYDRATION */))
}
}

})